import Loader from '@components/Loader/Loader';
import Tooltip from '@components/Tooltip/Tooltip';
import useNetworkStatus from '@hooks/use-network-status';
import useScrollBasedPin from '@hooks/use-scroll-based-pin';
import { motion } from 'motion/react';

import styles from './TopNav.module.scss';

interface TopNavProps {
  alwaysPinned?: boolean;
  showSearch?: boolean;
  showTasks?: boolean;
  size?: 'small' | 'large';
}

const TopNav: React.FC<React.PropsWithChildren<TopNavProps>> = ({
  children,
  alwaysPinned = true,
  size = 'small',
}) => {
  const isPinned = useScrollBasedPin(true, { pinThreshold: 150 });
  const isVisible = alwaysPinned || isPinned;

  const networkStatus = useNetworkStatus();

  return (
    <header className={styles['navbar']} data-show={isVisible} data-size={size}>
      <div className={styles['navbar-content']}>
        <div className={styles['nav-content']}>{children}</div>
      </div>

      {networkStatus === 'offline' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={styles['offline-warning']}>
          You are currently offline. Any changes that you make will not be persisted!
        </motion.div>
      )}
    </header>
  );
};

const PageOptions: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['page-options']}>{children}</div>;
};

interface PageOptionButtonProps {
  show?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  primary?: boolean;
  disabled?: boolean;
  explainer?: string;
}

const PageOptionButton = ({
  ref: forwardedRef,
  children,
  show = true,
  loading = false,
  primary = false,
  onClick,
  disabled = false,
  explainer,
}: React.PropsWithChildren<PageOptionButtonProps> & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  if (!show) {
    return null;
  }
  return (
    <Tooltip size="small" label={explainer} aria-label="Setting explanation" show={!!explainer}>
      <motion.button
        disabled={disabled}
        className={styles['page-option-button']}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onClick}
        ref={forwardedRef}
        data-primary={primary}
      >
        {!loading && children}
        {loading && <Loader />}
      </motion.button>
    </Tooltip>
  );
};

PageOptionButton.displayName = 'PageOptionButton';

const PageOptionButtonIcon: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <span className={styles['page-option-button-icon']}>{children}</span>
);

const PageOptionButtonText: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <span className={styles['page-option-button-text']}>{children}</span>
);

const AlignedContent: React.FC<React.PropsWithChildren<{ align?: 'left' | 'right' | 'center' }>> = ({
  align = 'left',
  children,
}) => (
  <div className={styles['alligned-content']} data-align={align}>
    {children}
  </div>
);

export default TopNav;
export { PageOptionButton, PageOptionButtonIcon, PageOptionButtonText, AlignedContent, PageOptions };
